import * as Api from '@ViewModels';
import { useInfiniteQuery } from '@tanstack/react-query';
import { IKeepInTouchScore } from '../../models/KeepInTouchAgent';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';
import { invalidateKeepInTouchAllScores } from './useKeepInTouchAllScores';

const BASE_QUERY_KEY = 'keepInTouchJobConfiguration/scores';

export function useKeepInTouchScores({
	configId,
	pageSize = 25,
	ownerOnly = false,
	minScore,
	enabled = true,
}: {
	configId: string;
	pageSize?: number;
	ownerOnly?: boolean;
	minScore?: number;
	enabled?: boolean;
}) {
	const userSession = useUserSession();
	return useInfiniteQuery<Api.IPagedCollection<IKeepInTouchScore>>({
		enabled: enabled && !!configId,
		queryFn: async ({ pageParam }) => {
			const params = new URLSearchParams({ pageSize: String(pageSize) });
			if (pageParam) params.set('pageToken', pageParam as string);
			if (ownerOnly) params.set('ownerOnly', 'true');
			if (minScore != null) params.set('minScore', String(minScore));
			return userSession.webServiceHelper.callAsync<Api.IPagedCollection<IKeepInTouchScore>>(
				`KeepInTouchJobConfiguration/${configId}/scores?${params}`,
				'GET'
			);
		},
		queryKey: [BASE_QUERY_KEY, configId, pageSize, ownerOnly, minScore],
		getNextPageParam: lastPage => lastPage.pageToken || undefined,
		initialPageParam: '',
		meta: {
			toastErrorMessage: 'Failed to load scores',
		},
	});
}

export const invalidateKeepInTouchScores = (configId?: string) => {
	if (configId) {
		reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY, configId] });
	} else {
		reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
	}
	invalidateKeepInTouchAllScores();
};
